import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";
import type { ObjectFitModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import {
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
  ComboboxTriggerButton,
} from "@replo/design-system/components/combobox/Combobox";
import { OBJECT_FIT_OPTIONS } from "@v2/features/modifiers/constants";
import { useOptimisticModifierValue } from "@v2/features/modifiers/hooks/useOptimisticModifierValue";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";

interface ObjectFitModifierProps {
  onChange: (modifier: ObjectFitModifierValue) => void;
}

// NOTE (Kevin, 2025-12-16): Set of valid object-fit values derived from OBJECT_FIT_OPTIONS
// for validating className parsing results. Typed as Set<string> to allow .has() checks with string values.
const VALID_OBJECT_FIT_VALUES: Set<string> = new Set(
  OBJECT_FIT_OPTIONS.map((option) => option.value),
);

const ObjectFitModifier: React.FC<ObjectFitModifierProps> = ({ onChange }) => {
  // NOTE (Kevin, 2025-12-16): Extract object-fit value from Tailwind classes.
  // Matches object-fit classes: object-fill, object-contain, object-cover, object-none, object-scale-down
  const extractObjectFitFromClassName = (
    className: string | null | undefined,
  ): ObjectFitModifierValue["value"] | null => {
    if (!className) {
      return null;
    }
    for (const cssClass of className.split(" ")) {
      if (cssClass.startsWith("object-")) {
        const objectFitValue = cssClass.slice(7);
        if (VALID_OBJECT_FIT_VALUES.has(objectFitValue)) {
          return objectFitValue as ObjectFitModifierValue["value"];
        }
      }
    }
    return null;
  };

  const objectFitFromElement =
    useOptimisticModifierValue({
      modifierType: { type: "objectFit", value: "cover" },
      extractor: (elementDesignation) => {
        return extractObjectFitFromClassName(
          elementDesignation.element.props?.className,
        );
      },
    }) ?? "cover";

  const [objectFit, setObjectFit] = useOverridableState(
    objectFitFromElement,
    React.useCallback(
      (value: ObjectFitModifierValue["value"]) => {
        onChange({ type: "objectFit", value });
      },
      [onChange],
    ),
  );

  const handleObjectFitChange = React.useCallback(
    (value: string | null) => {
      if (value && VALID_OBJECT_FIT_VALUES.has(value)) {
        setObjectFit(value as ObjectFitModifierValue["value"]);
      }
    },
    [setObjectFit],
  );

  const items: ComboboxItemWithValue[] = OBJECT_FIT_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
  }));

  const selectedItem = items.find((item) => item.value === objectFit) ?? null;

  return (
    <div className="object-fit-modifier">
      <ComboboxRoot
        items={items}
        value={selectedItem}
        onValueChange={(item) => {
          handleObjectFitChange(item?.value ?? null);
        }}
        itemToStringValue={(item) => String(item.label)}
        isItemEqualToValue={(a, b) => a.value === b.value}
        filter={null}
      >
        <ComboboxTriggerButton className="w-full">
          {selectedItem?.label ?? "Cover"}
        </ComboboxTriggerButton>
        <ComboboxContent sideOffset={8} side="bottom" align="center">
          <ComboboxList>
            {(item: ComboboxItemWithValue) => (
              <ComboboxItem key={item.value} {...item} value={item} />
            )}
          </ComboboxList>
        </ComboboxContent>
      </ComboboxRoot>
    </div>
  );
};

export default ObjectFitModifier;
